/**
 * F-27 pure placement math for the selection context toolbar. No DOM access —
 * unit-testable; the component converts the selection's board rect to screen
 * space (boardToScreen) and this decides where the pill goes.
 */

/** Rectangle in screen CSS px, relative to the board-canvas viewport origin. */
export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Gap between the selection edge and the pill (screen px, zoom-invariant). */
export const TOOLBAR_GAP_PX = 8;
/** Fixed pill height (screen px) — must match `.st-pill` height in app.css. */
export const TOOLBAR_HEIGHT_PX = 36;
/**
 * Horizontal clamp margin: the pill's centerX never leaves
 * [margin, viewport.width − margin], keeping (roughly half of) the pill
 * on-screen even when the selection's center is panned offscreen.
 */
export const TOOLBAR_CLAMP_MARGIN_PX = 90;
/**
 * The band along the viewport top the pill must stay OUT of: the two corner
 * pills (BoardScreen's `.board-bar`, 12 px down + ~34 px tall) float there,
 * above everything else, and a toolbar placed over the first row used to
 * vanish underneath them. Anything that would land in the band flips below
 * its selection instead (2026-09-03).
 */
export const TOOLBAR_TOP_INSET_PX = 52;

/** Viewport size of the board-canvas container, in screen CSS px. */
export interface ViewportSize {
  width: number;
  height: number;
}

export interface ToolbarPlacement {
  /** Horizontal CENTER of the pill (the pill renders translateX(-50%)). */
  centerX: number;
  /** Top edge of the pill. */
  top: number;
  /** Where the pill sits relative to the selection (popovers open away). */
  placement: "above" | "below";
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), hi);

/**
 * Where the pill wants to sit horizontally BEFORE the viewport clamp: over the
 * pointer that made the selection, held inside the selection's own horizontal
 * extent; the selection's centre when no pointer is known (a keyboard
 * selection, a programmatic one).
 *
 * Why the pointer and not the centre (2026-09-03): a whole-row selection is as
 * wide as the table, so its centre can be a screen away from the row handle
 * the user just clicked — the pill appeared "somewhere to the right", often
 * off-screen and pinned to the clamp margin, and read as missing. The hand is
 * already at the pointer; that is where the pill belongs. Clamping to the
 * selection keeps it from floating over cells that are not selected when the
 * pointer ended a drag just outside the range.
 */
export function preferredCenterX(selection: ScreenRect, pointerX: number | null): number {
  const center = selection.left + selection.width / 2;
  if (pointerX === null || !Number.isFinite(pointerX)) return center;
  return clamp(pointerX, selection.left, selection.left + selection.width);
}

/**
 * Miro placement: over the pointer that made the selection (centred when
 * there is none — `preferredCenterX`), `gap` px ABOVE it; flips BELOW when
 * the pill would clip past the viewport top. The caller feeds screen-space
 * rects, so pan/zoom tracking is free.
 *
 * The pill is always CLAMPED into the viewport (Miro pins it to the visible
 * portion of the selection): centerX stays within the horizontal margins,
 * and when NEITHER the above nor the below placement is visible — a
 * selection that vertically spans the viewport (whole-column select, F-12b)
 * or sits entirely offscreen — the pill pins to the nearest viewport edge
 * instead of rendering somewhere unreachable.
 */
export function placeToolbar(
  selection: ScreenRect,
  viewport: ViewportSize,
  toolbarHeight: number = TOOLBAR_HEIGHT_PX,
  gap: number = TOOLBAR_GAP_PX,
  topInset: number = TOOLBAR_TOP_INSET_PX,
  /** Screen X of the pointer that made the selection; null = none known. */
  pointerX: number | null = null,
): ToolbarPlacement {
  const centerX = clamp(
    preferredCenterX(selection, pointerX),
    TOOLBAR_CLAMP_MARGIN_PX,
    Math.max(TOOLBAR_CLAMP_MARGIN_PX, viewport.width - TOOLBAR_CLAMP_MARGIN_PX),
  );
  const maxTop = viewport.height - toolbarHeight;
  const above = selection.top - gap - toolbarHeight;
  if (above >= topInset && above <= maxTop) {
    return { centerX, top: above, placement: "above" };
  }
  const below = selection.top + selection.height + gap;
  if (below >= 0 && below <= maxTop) {
    return { centerX, top: below, placement: "below" };
  }
  // Neither natural placement is on-screen. Pin to the edge the selection
  // spills past: top edge when the selection extends above the viewport
  // (placement "below" so the popover opens into the viewport), bottom edge
  // otherwise (placement "above", ditto).
  if (above < topInset) {
    return { centerX, top: Math.max(gap, topInset), placement: "below" };
  }
  return { centerX, top: Math.max(gap, maxTop - gap), placement: "above" };
}

/**
 * Headroom a popover needs to open ABOVE the pill without clipping past the
 * viewport top — the bg palette's rendered height (~150px) plus margin.
 */
export const POPOVER_CLEARANCE_PX = 170;

/**
 * Which side of the pill a popover opens on. Away from the selection by
 * default (above the pill when the pill is above the selection), EXCEPT when
 * the pill sits too close to the viewport top for the popover to fit — then
 * it opens below (over the selection: covering beats unreachable).
 */
export function popoverSide(
  placement: ToolbarPlacement["placement"],
  pillTop: number,
  clearance: number = POPOVER_CLEARANCE_PX,
): "above" | "below" {
  return placement === "above" && pillTop >= clearance ? "above" : "below";
}
